/**
 * The draft's review in view as it scrolls (WS-22): how many issues are
 * open on it, the keys, the way back to Needs you — to the item after the
 * last one answered here — and, where there are several, the issues closed
 * in one go (decision 16): every open one of a rule kept or dismissed, or
 * every one left, with a reason if the person gives one.
 */

import { useState } from 'react';

import { Button, Group, Text, TextInput } from '@mantine/core';

import { words } from './format';
import { Kbd } from './panes/common';
import classes from './DraftView.module.css';

export interface IssueGroup {
  rule: string;
  count: number;
}

type Bulk = 'accepted' | 'dismissed';

interface DocumentIssueStripProps {
  /** The draft's open issues. */
  open: number;
  /** What Needs you holds. */
  needsYouTotal: number;
  onBack?: () => void;
  /** The open issues by rule, the most first. */
  groups?: IssueGroup[];
  /** Close every open issue of a rule (null: every one left) as given. */
  onResolveAll?: (status: Bulk, rule: string | null, note: string | null) => Promise<void>;
}

export function DocumentIssueStrip({
  open,
  needsYouTotal,
  onBack,
  groups = [],
  onResolveAll,
}: Readonly<DocumentIssueStripProps>) {
  const [asking, setAsking] = useState<{ status: Bulk; rule: string | null; count: number } | null>(
    null
  );
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const bulk = onResolveAll && open > 1;
  const several = groups.filter((g) => g.count > 1).slice(0, 3);
  const confirm = async () => {
    if (!asking || !onResolveAll) {
      return;
    }
    setBusy(true);
    try {
      await onResolveAll(asking.status, asking.rule, note.trim() || null);
      setAsking(null);
      setNote('');
    } finally {
      setBusy(false);
    }
  };
  const label = (status: Bulk, rule: string | null, count: number) =>
    `${status === 'accepted' ? 'Keep' : 'Dismiss'} all ${count}${rule ? ` ${words(rule)}` : ' remaining'}`;
  return (
    <Group gap="xs" wrap="wrap" className={classes.strip} data-testid="document-issue-strip">
      <Text size="xs" fw={600}>
        Draft review · {open === 0 ? 'every issue settled' : `${open} open`}
      </Text>
      {open > 0 && (
        <Text size="xs" c="dimmed">
          <Kbd>j</Kbd> next issue · <Kbd>k</Kbd> previous · <Kbd>1</Kbd> fix · <Kbd>2</Kbd> keep ·{' '}
          <Kbd>3</Kbd> dismiss · <Kbd>4</Kbd> fixed
        </Text>
      )}
      {onBack && (
        <Button
          size="compact-xs"
          variant="light"
          color="orange"
          ml="auto"
          onClick={onBack}
          data-testid="back-to-needs-you"
        >
          Back to Needs you{needsYouTotal > 0 ? ` · ${needsYouTotal}` : ''}
        </Button>
      )}
      {bulk && asking === null ? (
        <Group gap={4} wrap="wrap" w="100%" data-testid="issue-bulk">
          {several.flatMap((g) =>
            (['accepted', 'dismissed'] as const).map((status) => (
              <Button
                key={`${g.rule}:${status}`}
                size="compact-xs"
                variant="default"
                onClick={() => setAsking({ status, rule: g.rule, count: g.count })}
                data-testid={`issue-bulk-${status}-${g.rule}`}
              >
                {label(status, g.rule, g.count)}
              </Button>
            ))
          )}
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            onClick={() => setAsking({ status: 'dismissed', rule: null, count: open })}
            data-testid="issue-bulk-dismissed-all"
          >
            {label('dismissed', null, open)}
          </Button>
        </Group>
      ) : null}
      {bulk && asking !== null ? (
        <Group gap="xs" align="flex-end" w="100%" data-testid="issue-bulk-ask">
          <TextInput
            size="xs"
            placeholder="Why (optional) — kept on each issue and in Activity"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 200 }}
            data-testid="issue-bulk-note"
          />
          <Button
            size="xs"
            loading={busy}
            onClick={() => void confirm()}
            data-testid="issue-bulk-confirm"
          >
            {label(asking.status, asking.rule, asking.count)}
          </Button>
          <Button size="xs" variant="subtle" onClick={() => setAsking(null)}>
            Cancel
          </Button>
        </Group>
      ) : null}
    </Group>
  );
}
